// @flow

import { isPdbIdInComparison, numPdbsInComparison } from '../pdbComparison/selectors';
import { getDisableComparer } from '../routes/selectors';
import type { State as CompareSettingsState } from './reducers';
import type { State as ConfigurationState } from '../configuration';
import type { OrderFormState } from '../orderForm/types';
import type { State as PdbState } from '../pdb';
import type { State as PdbComparisonState } from '../pdbComparison';
import type { State as RouterState } from '../router';
import type { State as RoutesState } from '../routes';

type GlobalState = {
  compareSettings: CompareSettingsState,
  configuration: ConfigurationState,
  orderForm: OrderFormState,
  pdb: PdbState,
  pdbComparison: PdbComparisonState,
  router: RouterState,
  routes: RoutesState,
};

const getReducer = (state: GlobalState): CompareSettingsState => state.compareSettings;

export const compareEnabled = (state: GlobalState) => {
  if (getDisableComparer(state)) return false;
  return getReducer(state).enabled;
};

export const getAddComparisonText = (state: GlobalState) => (
  getReducer(state).add_comparison_text
);

export const getRemoveComparisonText = (state: GlobalState) => (
  getReducer(state).remove_comparison_text
);

export const getMaxItemsInComparison = (state: GlobalState) => (
  parseInt(getReducer(state).max_items_in_comparison, 10)
);

export const getAssortmentPageLink = (state: GlobalState) => (
  getReducer(state).assortment_page_link
);

export const getComparePageLink = (state: GlobalState) => (
  getReducer(state).compare_page_link
);

export const getCarSelectedText = (state: GlobalState) => (
  getReducer(state).car_selected
);

export const getCarAddButtonText = (state: GlobalState) => (
  getReducer(state).car_add_button_text
);

export const getCarsSelectedText = (state: GlobalState) => (
  getReducer(state).cars_selected
);

export const getButtonCompareCarsText = (state: GlobalState) => (
  getReducer(state).button_compare_cars
);

export const getButtonCompareCarText = (state: GlobalState) => (
  getReducer(state).button_compare_car
);

export const getCompareHeadingText = (state: GlobalState) => (
  getReducer(state).compare_heading
);

export const getTechSpecsHeadingText = (state: GlobalState) => (
  getReducer(state).tech_specs_heading
);

export const getButtonTechSpecsText = (state: GlobalState) => (
  getReducer(state).button_tech_specs
);

export const getButtonScrollToTopText = (state: GlobalState) => (
  getReducer(state).button_scroll_to_top
);

export const getButtonOrderText = (state: GlobalState) => (
  getReducer(state).button_order
);

export const getButtonModelInfoText = (state: GlobalState) => (
  getReducer(state).button_model_info
);

export const getButtonInspectPackagesText = (state: GlobalState) => (
  getReducer(state).button_inspect_packages
);

export const getRowHeadingModelText = (state: GlobalState) => (
  getReducer(state).row_heading_model
);

export const getRowHeadingBasepriceText = (state: GlobalState) => (
  getReducer(state).row_heading_baseprice
);

export const getRowHeadingBasepriceDescriptionText = (state: GlobalState) => (
  getReducer(state).row_heading_baseprice_description
);

export const getRowHeadingTypeText = (state: GlobalState) => (
  getReducer(state).row_heading_type
);

export const getRowHeadingTypeDescriptionText = (state: GlobalState) => (
  getReducer(state).row_heading_type_description
);

export const getRowHeadingColorText = (state: GlobalState) => (
  getReducer(state).row_heading_color
);

export const getRowHeadingDeliveryTimeText = (state: GlobalState) => (
  getReducer(state).row_heading_delivery_time
);

export const getRowHeadingDurationText = (state: GlobalState) => (
  getReducer(state).row_heading_duration
);

export const getRowHeadingKmpackText = (state: GlobalState) => (
  getReducer(state).row_heading_kmpack
);

export const getRowHeadingClaimFreeText = (state: GlobalState) => (
  getReducer(state).row_heading_claim_free
);

export const getRowHeadingPriceDifferenceText = (state: GlobalState) => (
  getReducer(state).row_heading_price_difference
);

export const getRowHeadingPricePerKmText = (state: GlobalState) => (
  getReducer(state).row_heading_price_per_km
);

export const getRowHeadingPriceText = (state: GlobalState) => (
  getReducer(state).row_heading_price
);

export const getRowHeadingMotorizationText = (state: GlobalState) => (
  getReducer(state).row_heading_motorization
);

export const getRowHeadingFuelText = (state: GlobalState) => (
  getReducer(state).row_heading_fuel
);

export const getRowHeadingPowerText = (state: GlobalState) => (
  getReducer(state).row_heading_power
);

export const getRowHeadingConsumptionText = (state: GlobalState) => (
  getReducer(state).row_heading_consumption
);

export const getRowHeadingTransmissionText = (state: GlobalState) => (
  getReducer(state).row_heading_transmission
);

export const getRowHeadingNrOfDoorsText = (state: GlobalState) => (
  getReducer(state).row_heading_nr_of_doors
);

export const getRowHeadingWeightText = (state: GlobalState) => (
  getReducer(state).row_heading_weight
);

export const getComparePopupTemplateBlacklist = (state: GlobalState): Array<string> => {
  const blacklist = getReducer(state).compare_popup_template_blacklist;
  if (!Array.isArray(blacklist)) {
    return [];
  }
  return blacklist;
};

export const canAddModelToCompare = (state: GlobalState) => (
  isPdbIdInComparison(state) || numPdbsInComparison(state) < getMaxItemsInComparison(state)
);
